import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CONFIG = {
  new_lead: { icon: 'UserPlus', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400', label: 'New Lead' },
  new_call: { icon: 'Phone', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', label: 'Call' },
  job_reminder: { icon: 'CalendarDays', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400', label: 'Reminder' },
  job_completed: { icon: 'CheckCircle', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400', label: 'Job Done' },
  job_update: { icon: 'Briefcase', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400', label: 'Job Update' },
  payment_received: { icon: 'DollarSign', color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', label: 'Payment' },
  review_request: { icon: 'Star', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400', label: 'Review' },
  team_invite: { icon: 'UserPlus', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', label: 'Team' },
  info: { icon: 'Bell', color: 'bg-muted text-muted-foreground', label: 'Info' },
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationsDrawer = ({ isOpen, onClose, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState('all');
  const { profile } = useAuth();
  const navigate = useNavigate();
  const channelRef = useRef(null);

  const PAGE_SIZE = 20;

  const fetchNotifications = async (reset = false) => {
    if (!profile?.org_id) return;
    setLoading(true);
    const currentPage = reset ? 0 : page;
    let query = supabase?.from('notifications')?.select('*')?.eq('org_id', profile?.org_id)?.order('created_at', { ascending: false })?.range(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE - 1);
    if (filter === 'unread') query = query?.eq('is_read', false);

    const { data, error } = await query;

    if (!error && data) {
      setNotifications(prev => reset ? data : [...prev, ...data]);
      setHasMore(data?.length === PAGE_SIZE);
      if (!reset) setPage(p => p + 1);
      else setPage(1);
    }
    setLoading(false);
  };

  const getUnreadCount = async () => {
    if (!profile?.org_id) return;
    const { count } = await supabase?.from('notifications')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.eq('is_read', false);
    onUnreadCountChange?.(count || 0);
  };

  useEffect(() => {
    if (!profile?.org_id) return;
    fetchNotifications(true);
    getUnreadCount();

    channelRef.current = supabase?.channel(`notifications_${profile?.org_id}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `org_id=eq.${profile?.org_id}`
      }, (payload) => {
        setNotifications(prev => [payload?.new, ...prev]);
        onUnreadCountChange?.(c => (c || 0) + 1);
      })?.subscribe();

    return () => {
      if (channelRef?.current) supabase?.removeChannel(channelRef?.current);
    };
  }, [profile?.org_id]);

  useEffect(() => {
    if (isOpen) fetchNotifications(true);
  }, [isOpen, filter]);

  const markAllRead = async () => {
    if (!profile?.org_id) return;
    await supabase?.from('notifications')?.update({ is_read: true })?.eq('org_id', profile?.org_id)?.eq('is_read', false);
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
    onUnreadCountChange?.(0);
  };

  const markRead = async (id) => {
    await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id);
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, is_read: true } : n));
    onUnreadCountChange?.(c => Math.max(0, (c || 0) - 1));
  };

  const handleNotificationClick = (notif) => {
    if (!notif?.is_read) markRead(notif?.id);
    if (notif?.link) navigate(notif?.link);
    onClose();
  };

  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[998]" onClick={onClose} />
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 bottom-0 w-80 bg-background border-l border-border shadow-2xl z-[999] flex flex-col"
            style={{ top: 'var(--header-height, 4rem)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-semibold text-foreground">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-primary hover:underline">Mark all read</button>
                )}
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex border-b border-border">
              {['all', 'unread']?.map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`flex-1 py-2 text-xs font-medium transition-colors capitalize ${
                    filter === f ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  {f}
                </button>
              ))}
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {loading && notifications?.length === 0 ? (
                <div className="space-y-3 p-4">
                  {[1,2,3]?.map(i => (
                    <div key={i} className="flex space-x-3 animate-pulse">
                      <div className="w-9 h-9 bg-muted rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : notifications?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                    <Icon name="Bell" size={20} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground">No notifications yet</p>
                  <p className="text-xs text-muted-foreground mt-1">We'll notify you when something happens</p>
                </div>
              ) : (
                <div>
                  {notifications?.map((notif) => {
                    const cfg = TYPE_CONFIG?.[notif?.type] || TYPE_CONFIG?.info;
                    return (
                      <button key={notif?.id} onClick={() => handleNotificationClick(notif)}
                        className={`w-full flex items-start space-x-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 text-left ${
                          !notif?.is_read ? 'bg-primary/5' : ''
                        }`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg?.color}`}>
                          <Icon name={cfg?.icon} size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <p className="text-sm font-medium text-foreground leading-tight">{notif?.title}</p>
                            {!notif?.is_read && (
                              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1 ml-2" />
                            )}
                          </div>
                          {notif?.body && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif?.body}</p>
                          )}
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-muted-foreground">{timeAgo(notif?.created_at)}</span>
                            {cfg?.label && (
                              <span className="text-xs text-muted-foreground">· {cfg?.label}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {hasMore && (
                    <div className="p-4">
                      <button onClick={() => fetchNotifications(false)} disabled={loading}
                        className="w-full py-2 text-sm text-primary hover:underline disabled:opacity-50">
                        {loading ? 'Loading...' : 'Load more'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationsDrawer;
